import {
  parse,
  View,
  type Config as VegaConfig,
  type LoggerInterface,
  type Spec,
} from "vega";
import { expressionInterpreter } from "vega-interpreter";
import { compile, type TopLevelSpec } from "vega-lite";
import { buildChartTheme, type VizThemeOptions } from "../theme/config.js";
import { FALLBACK_LIGHT, type VizPalette } from "../theme/tokens.js";
import type { ChartData } from "../types.js";
import { bindDataset } from "./dataBinding.js";
import { findEmptyDraw } from "./emptyDraw.js";
import { fitToHost, type HostSize } from "./fitToHost.js";
import { createBlockedLoader } from "./loader.js";
import type { CustomChartSpec } from "./types.js";

export interface RuntimeChart {
  /** A Vega spec — Vega-Lite is compiled to it — sized to its host. */
  spec: Spec;
  /** The config to parse {@link spec} with (Vega-Lite's is compiled in). */
  config: VegaConfig;
}

/**
 * Turns a validated Custom chart into the exact Vega spec the renderer runs:
 * dataset bound, theme applied, sized to the host. The browser embed and the
 * headless renderer both go through here, so what the tests render is what
 * users see.
 */
export function buildRuntimeChart(
  chart: CustomChartSpec,
  rows: ChartData,
  size: HostSize,
  palette: VizPalette,
  options?: VizThemeOptions,
): RuntimeChart {
  const theme = buildChartTheme(palette, options);
  const bound = bindDataset(chart, rows);
  if (chart.grammar === "vega-lite") {
    const compiled = compile({
      ...bound,
      width: "container",
      config: theme.vegaLite,
    } as unknown as TopLevelSpec).spec;
    return { spec: fitToHost(compiled, size), config: {} };
  }
  return { spec: fitToHost(bound as Spec, size), config: theme.vega };
}

/**
 * The runtime options every Custom chart view is created with: expressions
 * are interpreted from their AST rather than compiled with `new Function`
 * (the app's CSP has no 'unsafe-eval'), and the loader refuses every outside
 * resource.
 */
export const SAFE_VIEW_OPTIONS = {
  ast: true,
  expr: expressionInterpreter,
} as const;

/** A Vega logger that records errors (Vega logs dataflow errors rather than throwing). */
function collectingLogger(errors: string[]): LoggerInterface {
  const logger: LoggerInterface = {
    level: (() => logger) as LoggerInterface["level"],
    error: (...args: readonly unknown[]) => {
      errors.push(
        args
          .map((arg) => (arg instanceof Error ? arg.message : String(arg)))
          .join(" "),
      );
      return logger;
    },
    warn: () => logger,
    info: () => logger,
    debug: () => logger,
  };
  return logger;
}

export interface HeadlessRenderOptions {
  width: number;
  height: number;
  palette?: VizPalette;
  font?: string;
}

/**
 * Renders a Custom chart without a DOM — the same runtime spec, the same
 * CSP-safe expression interpreter, the same blocked loader as the browser.
 * Used by the test suite to prove charts render; usable server-side.
 */
export async function renderCustomChartHeadless(
  chart: CustomChartSpec,
  rows: ChartData,
  { width, height, palette = FALLBACK_LIGHT, font }: HeadlessRenderOptions,
): Promise<{
  view: View;
  svg: string;
  errors: string[];
  /** Why the chart drew no marks from rows that exist; null when it drew. */
  emptyDraw: string | null;
}> {
  const errors: string[] = [];
  const runtime = buildRuntimeChart(
    chart,
    rows,
    { height, fallbackWidth: width },
    palette,
    { font },
  );
  const view = new View(
    parse(runtime.spec, runtime.config, { ast: SAFE_VIEW_OPTIONS.ast }),
    {
      expr: SAFE_VIEW_OPTIONS.expr,
      loader: createBlockedLoader(),
      renderer: "none",
      logger: collectingLogger(errors),
    },
  );
  await view.runAsync();
  const svg = await view.toSVG();
  const emptyDraw =
    errors.length === 0 ? findEmptyDraw(view, runtime.spec, rows.length) : null;
  return { view, svg, errors, emptyDraw };
}

export interface RenderProof {
  /** Reasons to refuse the chart. */
  errors: string[];
  /** Doubts that cannot justify a refusal (see `complete`). */
  warnings: string[];
}

/**
 * Proves a validated chart actually renders: runs it headlessly on `rows` and
 * reports every error the dataflow raised or threw — the failures the schema
 * and parser cannot see (an expression reading a missing field, a transform
 * given the wrong shape) — and a chart that draws no marks from rows that
 * exist.
 *
 * Pass the rows the chart will actually render. Drawing nothing is only proof
 * of a broken chart over ALL of them: a year or top-N filter can rightly
 * exclude any subset. So with `complete: false` (the rows are a subset) an
 * empty draw is a warning, never an error.
 */
export async function verifyCustomChartRenders(
  chart: CustomChartSpec,
  rows: ChartData,
  { complete = true }: { complete?: boolean } = {},
): Promise<RenderProof> {
  try {
    const { errors, emptyDraw } = await renderCustomChartHeadless(chart, rows, {
      width: 400,
      height: 300,
    });
    if (!emptyDraw) return { errors, warnings: [] };
    return complete
      ? { errors: [...errors, emptyDraw], warnings: [] }
      : {
          errors,
          warnings: [
            `${emptyDraw} Checked on ${rows.length} of the dataset's rows only, so it may draw once all are there.`,
          ],
        };
  } catch (error) {
    return {
      errors: [error instanceof Error ? error.message : String(error)],
      warnings: [],
    };
  }
}
